import React, { useState } from 'react';
import './DataMigrationPanel.css';
import { MigrationResult, MigrationService } from '../services/MigrationService';
import { SchemaChange } from '../utils/SchemaComparator';
import LoadingSpinner from './LoadingSpinner';

const DataMigrationPanel: React.FC = () => {
  const [competitionCsv, setCompetitionCsv] = useState('');
  const [solutionCsv, setSolutionCsv] = useState('');
  const [userCsv, setUserCsv] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [migrationResult, setMigrationResult] = useState<MigrationResult | null>(null);
  const [activeTab, setActiveTab] = useState<'competition' | 'solution' | 'user'>('competition');
  
  const migrationService = new MigrationService();
  
  const handleMigration = async () => {
    setIsLoading(true);
    setMigrationResult(null);
    
    try {
      let result: MigrationResult;
      
      switch (activeTab) {
        case 'competition':
          if (!competitionCsv.trim()) {
            throw new Error('コンペティションCSVデータが入力されていません');
          }
          result = await migrationService.migrateCompetitions(competitionCsv);
          break;
        case 'solution':
          if (!solutionCsv.trim()) {
            throw new Error('ソリューションCSVデータが入力されていません');
          }
          result = await migrationService.migrateSolutions(solutionCsv);
          break;
        case 'user':
          if (!userCsv.trim()) {
            throw new Error('ユーザーCSVデータが入力されていません');
          }
          result = await migrationService.migrateUsers(userCsv);
          break;
        default:
          throw new Error('不明なタブが選択されています');
      }
      
      setMigrationResult(result);
    } catch (error) {
      setMigrationResult({
        success: false,
        error: error instanceof Error ? error.message : '不明なエラーが発生しました'
      });
    } finally {
      setIsLoading(false);
    }
  };
  
  const renderSchemaChanges = (schemaChanges?: SchemaChange) => {
    if (!schemaChanges) return null;
    
    return (
      <div className="schema-changes">
        <h4>スキーマ変更の詳細</h4>
        
        {schemaChanges.addedColumns.length > 0 && (
          <div className="change-section">
            <h5>追加されたカラム:</h5>
            <ul>
              {schemaChanges.addedColumns.map((column, index) => (
                <li key={index}>{column}</li>
              ))}
            </ul>
          </div>
        )}
        
        {schemaChanges.removedColumns.length > 0 && (
          <div className="change-section">
            <h5>削除されたカラム:</h5>
            <ul>
              {schemaChanges.removedColumns.map((column, index) => (
                <li key={index}>{column}</li>
              ))}
            </ul>
          </div>
        )}
        
        {schemaChanges.renamedColumns.length > 0 && (
          <div className="change-section">
            <h5>リネームされたカラム:</h5>
            <ul>
              {schemaChanges.renamedColumns.map((rename, index) => (
                <li key={index}>{rename.oldName} → {rename.newName}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };
  
  return (
    <div className="data-migration-panel">
      <h2>データマイグレーション</h2>
      <p className="description">
        CSVデータをインポートして、既存のデータとマージします。
        スキーマの変更が検出された場合は自動的にマイグレーションが行われます。
      </p>
      
      <div className="tabs">
        <button
          className={`tab-button ${activeTab === 'competition' ? 'active' : ''}`}
          onClick={() => setActiveTab('competition')}
        >
          コンペティション
        </button>
        <button
          className={`tab-button ${activeTab === 'solution' ? 'active' : ''}`}
          onClick={() => setActiveTab('solution')}
        >
          ソリューション
        </button>
        <button
          className={`tab-button ${activeTab === 'user' ? 'active' : ''}`}
          onClick={() => setActiveTab('user')}
        >
          ユーザー
        </button>
      </div>
      
      <div className="tab-content">
        {activeTab === 'competition' && (
          <div className="csv-input">
            <label htmlFor="competitionCsv">コンペティションCSVデータ:</label>
            <textarea
              id="competitionCsv"
              value={competitionCsv}
              onChange={(e) => setCompetitionCsv(e.target.value)}
              placeholder="Slug,Title,Subtitle,DeadlineDate,Tags,DataDomain,ParticipantsCount"
              rows={10}
            />
          </div>
        )}
        
        {activeTab === 'solution' && (
          <div className="csv-input">
            <label htmlFor="solutionCsv">ソリューションCSVデータ:</label>
            <textarea
              id="solutionCsv"
              value={solutionCsv}
              onChange={(e) => setSolutionCsv(e.target.value)}
              placeholder="CompetitionSlug,Rank,Score,UserIds,Summary"
              rows={10}
            />
          </div>
        )}
        
        {activeTab === 'user' && (
          <div className="csv-input">
            <label htmlFor="userCsv">ユーザーCSVデータ:</label>
            <textarea
              id="userCsv"
              value={userCsv}
              onChange={(e) => setUserCsv(e.target.value)}
              placeholder="UserId,CurrentRank,HighestRank"
              rows={10}
            />
          </div>
        )}
        
        <button
          className="migrate-button"
          onClick={handleMigration}
          disabled={isLoading}
        >
          マイグレーション実行
        </button>
      </div>
      
      {isLoading && (
        <div className="loading-container">
          <LoadingSpinner size="medium" message="マイグレーション中..." />
        </div>
      )}
      
      {migrationResult && (
        <div className={`migration-result ${migrationResult.success ? 'success' : 'error'}`}>
          <h3>{migrationResult.success ? 'マイグレーション成功' : 'マイグレーション失敗'}</h3>
          
          {migrationResult.success ? (
            <div className="success-details">
              <p>追加されたデータ: {migrationResult.addedCount}</p>
              <p>更新されたデータ: {migrationResult.updatedCount}</p>
              {renderSchemaChanges(migrationResult.schemaChanges)}
            </div>
          ) : (
            <div className="error-details">
              <p>{migrationResult.error}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DataMigrationPanel;
